// =============================================================================
// lib/learnProgress.ts — client-side progress store for gamified Learning.
//
// Tracks which lessons the user has PASSED (via the end-of-article quiz), total
// XP, and derives level + which tiers are unlocked. Persisted with AsyncStorage
// and exposed through a tiny subscribe() store so LearnScreen / the quiz UI stay
// in sync without a network round-trip. (A backend sync can layer on later; this
// makes the feature shippable now with zero prod DB changes.)
// =============================================================================
import AsyncStorage from '@react-native-async-storage/async-storage';
import { XP_PER_LESSON } from './learnQuiz';
import { api } from '../services/api';

export type LearnTier = 'beginner' | 'intermediate' | 'advanced';
export const TIER_ORDER: LearnTier[] = ['beginner', 'intermediate', 'advanced'];

const KEY = 'cryb.learn.progress.v1';

export interface LearnProgress {
  // slug -> best score fraction (0..1); presence means "attempted", >=pass means passed
  passed: Record<string, boolean>;
  scores: Record<string, number>;
  xp: number;
}

const empty: LearnProgress = { passed: {}, scores: {}, xp: 0 };
let state: LearnProgress = { ...empty };
let loaded = false;

type Listener = (p: LearnProgress) => void;
const listeners = new Set<Listener>();

function emit() {
  const snap = getProgress();
  listeners.forEach((l) => { try { l(snap); } catch {} });
}

export function subscribeProgress(l: Listener): () => void {
  listeners.add(l);
  return () => { listeners.delete(l); };
}

export function getProgress(): LearnProgress {
  return { passed: { ...state.passed }, scores: { ...state.scores }, xp: state.xp };
}

// XP is DERIVED from passed lessons so client + server never drift.
function deriveXp(): number {
  return Object.values(state.passed).filter(Boolean).length * XP_PER_LESSON;
}

// Merge a server aggregate into local state: a lesson passed ANYWHERE stays
// passed (offline-first union), best score wins. Returns true if state changed.
function mergeServer(agg: { passed?: Record<string, boolean>; scores?: Record<string, number> } | undefined): boolean {
  if (!agg) return false;
  let changed = false;
  // Guard against a malformed server payload (null/array/scalar) — only iterate
  // plain objects, else Object.entries would throw or produce junk keys.
  const isObj = (v: any) => v && typeof v === 'object' && !Array.isArray(v);
  const passed = isObj(agg.passed) ? agg.passed! : {};
  const scores = isObj(agg.scores) ? agg.scores! : {};
  for (const [slug, p] of Object.entries(passed)) {
    if (p && !state.passed[slug]) { state.passed[slug] = true; changed = true; }
  }
  for (const [slug, sc] of Object.entries(scores)) {
    if (typeof sc === 'number' && (state.scores[slug] ?? 0) < sc) { state.scores[slug] = sc; changed = true; }
  }
  const xp = deriveXp();
  if (xp !== state.xp) { state.xp = xp; changed = true; }
  return changed;
}

export async function loadProgress(): Promise<LearnProgress> {
  if (loaded) return getProgress();
  try {
    const raw = await AsyncStorage.getItem(KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      state = {
        passed: parsed?.passed ?? {},
        scores: parsed?.scores ?? {},
        xp: typeof parsed?.xp === 'number' ? parsed.xp : 0,
      };
    }
  } catch { /* first run / corrupt → empty */ }
  // Normalise XP to the derived value (older caches stored an incremented xp).
  state.xp = deriveXp();
  loaded = true;
  emit();
  // Pull server progress in the background and merge (cross-device sync). Never
  // blocks the UI; failure just leaves the local cache in place.
  void pullProgress();
  return getProgress();
}

// Fetch the server aggregate and merge it in (server persists across devices).
export async function pullProgress(): Promise<void> {
  try {
    const res = await api<{ passed?: Record<string, boolean>; scores?: Record<string, number>; xp?: number }>(
      '/learn/progress',
    );
    if (res.success && mergeServer(res.data)) {
      await persist();
      emit();
    }
  } catch { /* offline → keep local */ }
}

async function persist() {
  try { await AsyncStorage.setItem(KEY, JSON.stringify(state)); } catch {}
}

// Record a quiz attempt. Awards XP only the FIRST time a lesson is passed.
// Returns { firstPass, xpAwarded }.
export async function recordQuiz(
  slug: string,
  scoreFraction: number,
  passed: boolean,
): Promise<{ firstPass: boolean; xpAwarded: number }> {
  if (!loaded) await loadProgress();
  const prevBest = state.scores[slug] ?? 0;
  if (scoreFraction > prevBest) state.scores[slug] = scoreFraction;

  const wasPassed = !!state.passed[slug];
  let firstPass = false;
  let xpAwarded = 0;
  if (passed && !wasPassed) {
    state.passed[slug] = true;
    firstPass = true;
    xpAwarded = XP_PER_LESSON;
  }
  state.xp = deriveXp();
  await persist();
  emit();
  // Push to the server (best-effort) so progress follows the user across devices.
  // Fire-and-forget; the local cache already reflects the change offline.
  void api('/learn/progress', {
    method: 'POST',
    body: JSON.stringify({ slug, score: scoreFraction, passed }),
  }).then((res) => {
    if (res.success && mergeServer(res.data as any)) { persist(); emit(); }
  }).catch(() => {});
  return { firstPass, xpAwarded };
}

export function isPassed(slug?: string): boolean {
  return !!(slug && state.passed[slug]);
}

// Level from XP — every 150 XP (3 lessons) is a level.
export function levelForXp(xp: number): { level: number; intoLevel: number; perLevel: number } {
  const perLevel = XP_PER_LESSON * 3;
  const level = Math.floor(xp / perLevel) + 1;
  const intoLevel = xp % perLevel;
  return { level, intoLevel, perLevel };
}

// A tier is unlocked when EVERY lesson in the previous tier has been passed.
// beginner is always unlocked. `byTier` maps tier -> its article slugs.
// The knowledge check is now OPTIONAL (client ask): passing a quiz is NOT required
// to open the next lesson/tier. Everything is accessible; the quiz stays purely as
// an XP reward, not a gate. These return true so nothing locks; signatures kept
// (still called by LearnScreen/XP display + server sync).
export function tierUnlocked(
  _tier: LearnTier,
  _byTier: Record<LearnTier, string[]>,
): boolean {
  return true;
}

export function lessonUnlocked(_indexInTier: number, _tierSlugs: string[]): boolean {
  return true;
}

// SINGLE SOURCE OF TRUTH for "can the user open this article?" — usable from ANY
// entry point (Learn grid, home rail, "more lessons" carousel, deep link) so the
// unlock gate can't be bypassed. An already-passed lesson stays open (re-readable).
// An unknown slug (featured/legacy not in any tier) is treated as OPEN — we never
// block content we can't place. `byTier` maps tier -> ordered slugs.
export function articleUnlocked(slug: string | undefined, byTier: Record<LearnTier, string[]>): boolean {
  if (!slug) return true;
  if (state.passed[slug]) return true; // already cleared → always re-openable
  for (const tier of TIER_ORDER) {
    const slugs = byTier[tier] || [];
    const idx = slugs.indexOf(slug);
    if (idx === -1) continue;                     // not in this tier
    if (!tierUnlocked(tier, byTier)) return false; // whole tier still locked
    return lessonUnlocked(idx, slugs);            // earlier lessons in tier passed?
  }
  return true; // slug not found in any tier → don't gate unknown content
}
